import * as React from 'react'
import { Input as InputPrimitive } from '@base-ui/react/input'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

// untitledui-reference.md "Input field" (master 85:1269, frame 1090:57817) —
// Size × Type × Destructive × State is the source for every number below.
// Ladder: sm 36 (`default` alias, byte-identical string so the two can never
// drift) · md 40 · lg 44 — matching Button's T3 ladder. Value text scales with
// height (DES-52: a new size rung scales font-size and icon-size, not just the
// box) — sm 14px, md/lg 16px.
//
// `default` aliases **sm**, not md. The mock's dashboard field (Dashboard
// 33:2249) is 36/14, which is the kit's `sm`; this file shipped with `default`
// pointing at `md`, so 33 of 37 call sites rendered 40/16 — a size the design
// never asked for. Fixing it here rather than passing `inputSize="sm"` at each
// call site is deliberate: a value repeated at thirty call sites is thirty
// values that agree today, which is exactly how the onboarding shell width
// drifted from its own header (DES-13). The two hero surfaces that genuinely
// want a bigger field opt in explicitly with `lg`.
const INPUT_SIZE_SM = 'h-9 px-3 py-2 text-sm'

const inputVariants = cva(
  // border-border (an ink-based alpha hairline — color-mix against --ink-900
  // light / --d-text dark, repointed by brand-token-migration T1) replaces
  // border-input's separate alpha hairline — one token for the job without
  // breaking DES-12 (both themes stay first-class). Focus is a border-color
  // swap to `--lime-500` plus a soft lime glow (the `focus-lime` utility,
  // globals.css) — verbatim from the Claude Design source's literal input
  // field markup. Not the ink-based `focus-ring`/`--ring` treatment: that
  // effect is scoped by the master to buttons, tabs, toggles and checkboxes,
  // and a lime accent stays visible here specifically because this field is
  // never lime-filled at rest (unlike a lime CTA, where an ink ring is used
  // instead so it doesn't vanish against its own fill).
  // Placeholder uses --placeholder (#737373, the kit's true placeholder
  // colour) — not --muted-foreground (a separate, lower-contrast ink step,
  // the kit's *hint*-text colour this file used to render instead).
  // No hover anywhere: the master defines Placeholder · Filled · Focused ·
  // Disabled only (DES-52 note — this family has no hover state to add).
  'flex w-full min-w-0 rounded-md border border-border bg-background text-foreground outline-none transition-[border-color] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-placeholder focus-visible:focus-lime disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive/40 aria-invalid:focus-visible:focus-lime-error dark:bg-input/30',
  {
    variants: {
      // Named `inputSize`, not `size` — `<input>` carries a native HTML
      // `size` attribute (visible character width, typed `number`) via
      // `React.ComponentProps<'input'>`, and a cva variant can't share that
      // name without a type collision (`Button` has no such attribute to
      // collide with). One real call site relies on the native attribute —
      // `quick-scan-client.tsx:480` passes `size={20}` — so the attribute
      // stays available and this variant uses a name that can't shadow it.
      inputSize: {
        // `default` is a byte-identical alias for `sm` (Button's T3 pattern) —
        // shares the string so the two entries can never drift apart.
        default: INPUT_SIZE_SM,
        sm: INPUT_SIZE_SM,
        md: 'h-10 px-3 py-2 text-base',
        lg: 'h-11 px-3.5 py-2.5 text-base',
      },
    },
    defaultVariants: {
      inputSize: 'default',
    },
  }
)

// Full, static class strings — Tailwind's scanner needs the complete
// `[&_svg]:size-N` token in source, not one built by string interpolation.
// Every `default` entry below mirrors `sm`, matching the cva alias above. These
// five tables and the cva variant have to agree on what `default` means, and
// nothing enforces that — a `default` still pointing at `md` here would leave a
// 36px field with a 20px glyph inset for a 40px box.
const ICON_SIZE_BY_INPUT_SIZE: Record<'sm' | 'md' | 'lg' | 'default', string> = {
  sm: '[&_svg]:size-4',
  md: '[&_svg]:size-5',
  lg: '[&_svg]:size-5',
  default: '[&_svg]:size-4',
}

const LEADING_PAD_BY_INPUT_SIZE: Record<'sm' | 'md' | 'lg' | 'default', string> = {
  sm: 'pl-8',
  md: 'pl-10',
  lg: 'pl-11',
  default: 'pl-8',
}

const TRAILING_PAD_BY_INPUT_SIZE: Record<'sm' | 'md' | 'lg' | 'default', string> = {
  sm: 'pr-8',
  md: 'pr-10',
  lg: 'pr-11',
  default: 'pr-8',
}

const LEADING_INSET_BY_INPUT_SIZE: Record<'sm' | 'md' | 'lg' | 'default', string> = {
  sm: 'left-2.5',
  md: 'left-3',
  lg: 'left-3.5',
  default: 'left-2.5',
}

const TRAILING_INSET_BY_INPUT_SIZE: Record<'sm' | 'md' | 'lg' | 'default', string> = {
  sm: 'right-2.5',
  md: 'right-3',
  lg: 'right-3.5',
  default: 'right-2.5',
}

interface InputProps extends React.ComponentProps<'input'>, VariantProps<typeof inputVariants> {
  /**
   * Leading icon slot (search glyph, unit prefix). ReactNode only — never a
   * render callback (ARC-03: Client Components must not accept function props
   * across the RSC boundary). Purely decorative; carries `aria-hidden`.
   */
  leadingIcon?: React.ReactNode
  /**
   * Trailing icon slot (status glyph, unit suffix, clear button host). Same
   * ReactNode contract as `leadingIcon`. For the destructive alert glyph the
   * master draws automatically on `aria-invalid`, pass it explicitly here —
   * this slot does not auto-inject it.
   */
  trailingIcon?: React.ReactNode
}

/**
 * Input — the canonical single-line text field. Sizes `sm`(36, `default`)/
 * `md`(40)/`lg`(44). States: Placeholder · Filled · Focused · Disabled ·
 * Destructive (`aria-invalid`) — no hover, the master defines none for this
 * component family. Focus renders as a `--lime-500` border-color swap plus a
 * soft lime glow (`focus-lime`, globals.css) — never the button-family
 * ink ring (DES-39 — the replacement must stay clearly visible in both
 * themes).
 *
 * WHEN: any single-line text entry. Pass `leadingIcon`/`trailingIcon` for a
 * search, unit, or status glyph inside the field.
 * NEVER hardcode height, border, or focus treatment at the call site — use
 * `inputSize` and `aria-invalid`. NEVER pass a render-prop to either icon
 * slot. Note `inputSize`, not `size` — `size` stays the native HTML
 * character-width attribute (see the cva variant comment above).
 */
function Input({ className, type, inputSize, leadingIcon, trailingIcon, ...props }: InputProps) {
  const resolvedSize = inputSize ?? 'default'
  const hasLeading = leadingIcon != null
  const hasTrailing = trailingIcon != null

  const inputElement = (
    <InputPrimitive
      type={type}
      data-slot="input"
      className={cn(
        inputVariants({ inputSize }),
        hasLeading && LEADING_PAD_BY_INPUT_SIZE[resolvedSize],
        hasTrailing && TRAILING_PAD_BY_INPUT_SIZE[resolvedSize],
        className
      )}
      {...props}
    />
  )

  if (!hasLeading && !hasTrailing) {
    return inputElement
  }

  return (
    <div className="relative flex w-full items-center">
      {hasLeading && (
        <span
          aria-hidden="true"
          data-slot="input-icon-leading"
          className={cn(
            'pointer-events-none absolute flex items-center text-muted-foreground',
            LEADING_INSET_BY_INPUT_SIZE[resolvedSize],
            ICON_SIZE_BY_INPUT_SIZE[resolvedSize]
          )}
        >
          {leadingIcon}
        </span>
      )}
      {inputElement}
      {hasTrailing && (
        <span
          aria-hidden="true"
          data-slot="input-icon-trailing"
          className={cn(
            'pointer-events-none absolute flex items-center text-muted-foreground',
            TRAILING_INSET_BY_INPUT_SIZE[resolvedSize],
            ICON_SIZE_BY_INPUT_SIZE[resolvedSize]
          )}
        >
          {trailingIcon}
        </span>
      )}
    </div>
  )
}

export { Input, inputVariants }
